// 11 Sep 1906
//
// REFACTORED TO USE CENTRALIZED HOOK
// All complex vitals logic moved to useVitalsForm hook for easier maintenance
//
import { useState } from 'react'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  MenuItem,
  Box,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DoneIcon from '@mui/icons-material/Done'
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined'
import { Spinner } from 'react-bootstrap'
import styled from 'styled-components'
import { postCPDataWithParams } from 'src/clinic/services'
import { useVitalsForm } from 'src/reducers/vitalsFormSettings/hooks/useVitalsForm'

// Styled components – modernized for clarity and usability
const RecordVitalItemSpan = styled.span`
  min-width: 160px;
  display: flex;
  align-items: center;
  font-weight: 500;
  color: #374151;
  font-size: 0.9375rem;
`

const VitalRow = styled.div`
  display: flex;
  align-items: ${(props) => (props.$isTextArea ? 'flex-start' : 'center')};
  margin-bottom: 20px;
  gap: 8px;

  ${(props) =>
    props.$isTextArea &&
    `
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
  `}
`

const UnitSelector = styled.div`
  display: flex;
  align-items: center;
  min-width: 72px;
`

// Legacy utility functions - now handled by vitalsUtils

export const VitalsComponentEntryDialog = ({
  patientId,
  clinicId,
  bookedEventId,
  closeFunction,
  vitalsFormGetApiSignature,
  callbackFunctionAfterSave,
  existingVitalData,
}) => {
  const [saving, setSaving] = useState(false)

  // Use centralized vitals hook with all the complex logic (pre-fill with latest vitals when editing)
  const {
    vitalsFields,
    vitalData,
    selectedUnits,
    hasData,
    handleInputChange,
    handleUnitChange,
    getDisplayValue,
    getDefaultUnit,
    setVitalData,
  } = useVitalsForm({
    vitalsFormGetApiSignature: vitalsFormGetApiSignature,
    existingVitalData: existingVitalData ?? null,
  })

  const saveVital = async () => {
    if (!hasData) {
      closeFunction()
      return
    }

    setSaving(true)
    const toSendData = { ...vitalData, patientId, clinicId }
    if (bookedEventId) {
      toSendData.bookedEventId = bookedEventId
    }

    try {
      await postCPDataWithParams('/api/secure/patient/vitals/save', toSendData, {})
      await callbackFunctionAfterSave()
      closeFunction()
    } catch (error) {
      console.error('Failed to save vitals:', error)
    } finally {
      setSaving(false)
    }
  }

  const renderVitalField = (field) => {
    const isDisabled = saving || field.ui?.readOnly
    const selectedUnit = selectedUnits[field.id] || getDefaultUnit(field)
    const displayValue = getDisplayValue(field)

    // Handle grouped fields (like blood pressure)
    if (field.ui?.groupId === 'bp') {
      const otherBpField = vitalsFields.find((f) => f.ui?.groupId === 'bp' && f.id !== field.id)

      if (field.ui?.groupHeader && otherBpField) {
        const otherDisplayValue = getDisplayValue(otherBpField)

        return (
          <VitalRow key={field.id}>
            <RecordVitalItemSpan>{field.ui.groupLabel || field.label}</RecordVitalItemSpan>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                type="number"
                value={displayValue || ''}
                disabled={isDisabled}
                onChange={(e) => handleInputChange(field.id, e.target.value, selectedUnit)}
                sx={{ width: 88 }}
                slotProps={{ input: { sx: { textAlign: 'center' } } }}
              />
              <span style={{ color: '#6b7280', fontWeight: 500 }}>/</span>
              <TextField
                size="small"
                type="number"
                value={otherDisplayValue || ''}
                disabled={isDisabled}
                onChange={(e) => handleInputChange(otherBpField.id, e.target.value, selectedUnit)}
                sx={{ width: 88 }}
                slotProps={{ input: { sx: { textAlign: 'center' } } }}
              />
              <UnitSelector>
                {field.units && field.units.length > 1 ? (
                  <TextField
                    size="small"
                    select
                    value={selectedUnit?.code || ''}
                    onChange={(e) => {
                      const newUnit = field.units.find((u) => u.code === e.target.value)
                      handleUnitChange(field.id, newUnit)
                      handleUnitChange(otherBpField.id, newUnit)
                    }}
                    sx={{ minWidth: 72 }}
                  >
                    {field.units.map((unit) => (
                      <MenuItem key={unit.code} value={unit.code}>
                        {unit.label}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>{selectedUnit?.label}</span>
                )}
              </UnitSelector>
            </Box>
          </VitalRow>
        )
      } else if (!field.ui?.groupHeader) {
        // Skip rendering the second BP field as it's handled above
        return null
      }
    }

    // Check if this field should render as textarea based on API configuration
    const isTextAreaField = field.ui?.input === 'textarea'

    if (isTextAreaField) {
      return (
        <VitalRow key={field.id} $isTextArea>
          <RecordVitalItemSpan>{field.label}</RecordVitalItemSpan>
          <TextField
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            value={displayValue || ''}
            disabled={isDisabled}
            placeholder={`Enter ${field.label.toLowerCase()}...`}
            onChange={(e) => {
              setVitalData((prev) => ({ ...prev, [field.id]: e.target.value }))
            }}
            variant="outlined"
            size="small"
          />
        </VitalRow>
      )
    }

    return (
      <VitalRow key={field.id}>
        <RecordVitalItemSpan>{field.label}</RecordVitalItemSpan>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            type="number"
            value={displayValue || ''}
            disabled={isDisabled || field.type === 'calculated'}
            onChange={(e) => {
              if (field.ui?.max && e.target.value > field.ui.max) return
              handleInputChange(field.id, e.target.value, selectedUnit)
            }}
            sx={{ width: 120 }}
          />
          <UnitSelector>
            {field.units && field.units.length > 1 && field.type !== 'calculated' ? (
              <TextField
                size="small"
                select
                value={selectedUnit?.code || ''}
                onChange={(e) => {
                  const newUnit = field.units.find((u) => u.code === e.target.value)
                  handleUnitChange(field.id, newUnit)
                }}
                sx={{ minWidth: 72 }}
              >
                {field.units.map((unit) => (
                  <MenuItem key={unit.code} value={unit.code}>
                    {unit.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>{selectedUnit?.label}</span>
            )}
            {field.type === 'calculated' && (
              <span style={{ fontSize: '0.75rem', color: '#059669', marginLeft: 6 }}>(calculated)</span>
            )}
          </UnitSelector>
        </Box>
      </VitalRow>
    )
  }

  return (
    <Dialog
      open
      onClose={closeFunction}
      PaperProps={{
        sx: {
          margin: 2,
          maxWidth: 'none',
          borderRadius: 2,
          minWidth: 520,
          maxHeight: 'calc(100vh - 32px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
          px: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'grey.50',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <MonitorHeartOutlinedIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Box component="span" sx={{ fontWeight: 600, fontSize: '1.25rem', color: 'grey.800' }}>
            Record Vitals
          </Box>
        </Box>
        <IconButton
          aria-label="Close"
          onClick={closeFunction}
          sx={{
            color: 'grey.600',
            '&:hover': { backgroundColor: 'grey.200' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: 3,
          py: 2.5,
        }}
      >
        <Box sx={{ maxWidth: 480 }}>
          {vitalsFields.map(renderVitalField).filter(Boolean)}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'grey.50',
          gap: 1,
        }}
      >
        <Button variant="outlined" onClick={closeFunction} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={saveVital}
          disabled={saving}
          startIcon={saving ? <Spinner variant="light" size="sm" /> : <DoneIcon />}
        >
          {saving ? 'Saving…' : 'Save Vitals'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
